import {
  AppwideDrawerContentType,
  Box,
  Button,
  CustomerUserConfigLayoutNavigationTypes,
  CustomerUserConfigLayoutType,
  DEFAULT_WL_ORG_CONFIG,
  Dialog,
  Flex,
  FormControlSizes,
  FormGroup,
  IconName,
  LocaleSelector,
  SearchSelect,
  ThemeToggle,
  Toggle,
  WL_ACTION,
  getStringLabel,
  useAppwideDrawerContext,
  useDynamicCallback,
  useIntlContext,
  useWLLayoutType,
  useWLOrgConfigContext,
  type CustomerUserConfigNavigationItem,
  type DialogProps,
  type ThemeTypes,
  type WLOrgConfig,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { DemoColorGenerator } from './DemoColorGenerator';
import {
  getLayoutArrangementOptions,
  isArrangementValidForLayout,
  type LayoutArrangementOption,
} from './demoLayoutOverrides';

type ThemeOption = {
  label: string;
  value: string;
  logo: WLOrgConfig['logo'];
  collapsedLogo: WLOrgConfig['collapsedLogo'];
};

export const DemoSettingsDialog = ({
  themeType,
  onChangeThemeType,
  ...props
}: DialogProps & {
  themeType: ThemeTypes;
  onChangeThemeType: (themeType: ThemeTypes) => void;
}) => {
  const { overrides, setOverrides } = useWLOrgConfigContext();
  const { layoutType } = useWLLayoutType();
  const [navigationOptions] = useState(
    Object.keys(CustomerUserConfigLayoutNavigationTypes) as Array<CustomerUserConfigLayoutNavigationTypes>
  );

  const [layoutOptions] = useState(Object.keys(CustomerUserConfigLayoutType) as CustomerUserConfigLayoutType[]);

  const [themeOptions] = useState<ThemeOption[]>([
    {
      label: 'CryptoCo OTC',
      value: 'DarkTalosTheme',
      logo: { dark: 'cryptoco-otc-logo-d.svg', light: 'cryptoco-otc-logo-l.svg' },
      collapsedLogo: { dark: 'cryptoco-otc-icon.svg', light: 'cryptoco-otc-icon.svg' },
    },
    {
      label: 'Transparency',
      value: 'DarkTransparencyTheme',
      logo: { dark: 'transparency-logo-dark.svg', light: 'transparency-logo-light.svg' },
      collapsedLogo: { dark: 'transparency-collapsed-logo-dark.svg', light: 'transparency-collapsed-logo-light.svg' },
    },
    {
      label: 'Gradients',
      value: 'DarkGradientsTheme',
      logo: { dark: 'gradients-logo.svg', light: 'gradients-logo.svg' },
      collapsedLogo: { dark: 'gradients-collapsed-logo.svg', light: 'gradients-collapsed-logo.svg' },
    },
    {
      label: 'Conservative',
      value: 'DarkConservativeTheme',
      logo: { dark: 'conservative-logo-dark.svg', light: 'conservative-logo-light.svg' },
      collapsedLogo: { dark: 'conservative-collapsed-logo-dark.svg', light: 'conservative-collapsed-logo-light.svg' },
    },
  ]);

  // useWLLayoutType already prefers demo overrides.layout, then the user's saved layoutType,
  // then the org default — so Trading still shows arrangements by default, and Swap/SimpleRFQ stay consistent.
  const layoutArrangementOptions = useMemo(() => getLayoutArrangementOptions(layoutType), [layoutType]);

  const selectedLayoutArrangement = useMemo(
    () => layoutArrangementOptions.find(option => option.value === overrides.layoutArrangement),
    [layoutArrangementOptions, overrides.layoutArrangement]
  );

  const handleThemeChange = useCallback(
    (value?: ThemeOption) => {
      if (value?.value) {
        setOverrides({ ...overrides, theme: value?.value, logo: value?.logo, collapsedLogo: value?.collapsedLogo });
      } else {
        const nextOverrides = { ...overrides };
        delete nextOverrides.theme;
        delete nextOverrides.logo;
        delete nextOverrides.collapsedLogo;
        setOverrides(nextOverrides);
      }
    },
    [overrides, setOverrides]
  );

  const handleLayoutChange = useDynamicCallback((layout: CustomerUserConfigLayoutType | undefined) => {
    const nextOverrides = { ...overrides };
    if (layout) {
      nextOverrides.layout = layout;
      if (!isArrangementValidForLayout(layout, nextOverrides.layoutArrangement)) {
        delete nextOverrides.layoutArrangement;
      }
    } else {
      delete nextOverrides.layout;
      delete nextOverrides.layoutArrangement;
    }

    setOverrides(nextOverrides);
  });

  const handleLayoutArrangementChange = useDynamicCallback((arrangement: LayoutArrangementOption | undefined) => {
    if (arrangement) {
      setOverrides({ ...overrides, layoutArrangement: arrangement.value });
    } else {
      const nextOverrides = { ...overrides };
      delete nextOverrides.layoutArrangement;
      setOverrides(nextOverrides);
    }
  });

  // We only want to show the trading layout.
  const exampleNavItems = useMemo(
    () =>
      EXAMPLE_NAV_ITEMS.filter(ni => {
        return ni.layoutComponent !== CustomerUserConfigLayoutType.TradingLayout;
      }),
    []
  );
  const { openDrawer } = useAppwideDrawerContext();
  const onClickThemeGenerator = useDynamicCallback(() => {
    props.close();
    openDrawer({
      title: 'Custom Theme Generation',
      renderContent: () => <DemoColorGenerator />,
      type: AppwideDrawerContentType.ThemeColorGeneration,
    });
  });

  const { locale, setLocale } = useIntlContext();

  return (
    <Dialog {...props} width={360} autoFocusFirstElement={false} alignContent="left">
      <FormGroup label="Navigation Type">
        <SearchSelect
          showClear={true}
          options={navigationOptions}
          selection={overrides.navigationType}
          onChange={value => setOverrides({ ...overrides, navigationType: value })}
          getLabel={getStringLabel}
          portalize={true}
        />
      </FormGroup>
      <FormGroup label="Theme">
        <SearchSelect
          showClear={true}
          getLabel={o => o.label}
          options={themeOptions}
          selection={themeOptions.find(to => to.value === overrides.theme)}
          onChange={handleThemeChange}
          portalize={true}
        />
      </FormGroup>
      <FormGroup label="Layout">
        <SearchSelect
          showClear={true}
          getLabel={getStringLabel}
          options={layoutOptions}
          selection={overrides.layout}
          onChange={handleLayoutChange}
          portalize={true}
        />
      </FormGroup>
      {layoutArrangementOptions.length > 0 && (
        <FormGroup label="Layout Arrangement">
          <SearchSelect
            showClear={true}
            getLabel={option => option.label}
            options={layoutArrangementOptions}
            selection={selectedLayoutArrangement}
            onChange={handleLayoutArrangementChange}
            portalize={true}
          />
        </FormGroup>
      )}
      <FormGroup inline justifyContent="flex-end">
        <LocaleSelector locale={locale} setLocale={setLocale} />
      </FormGroup>
      <FormGroup
        inline
        label={
          <Flex justifyContent="center" gap="spacing4">
            Theme type{' '}
            <Button size={FormControlSizes.Xxs} onClick={onClickThemeGenerator} endIcon={IconName.ExternalLink}>
              Custom
            </Button>
          </Flex>
        }
        justifyContent="space-between"
      >
        <Box>
          <ThemeToggle themeType={themeType} onChangeThemeType={onChangeThemeType} size={FormControlSizes.Small} />
        </Box>
      </FormGroup>
      <FormGroup inline label="Show menu items" justifyContent="space-between" alignItems="center">
        <Toggle
          checked={!!overrides.navigationItems}
          onChange={checked => setOverrides({ ...overrides, navigationItems: checked ? exampleNavItems : undefined })}
        />
      </FormGroup>
      <FormGroup inline label="Order form toggle" justifyContent="space-between" alignItems="center">
        <Toggle
          checked={!!overrides.orderFormToggle}
          onChange={checked => setOverrides({ ...overrides, orderFormToggle: checked })}
        />
      </FormGroup>
      <FormGroup inline label="Show spread" justifyContent="space-between" alignItems="center">
        <Toggle
          checked={!!overrides.showSpreadOnVolumeLadder}
          onChange={checked => setOverrides({ ...overrides, showSpreadOnVolumeLadder: checked })}
        />
      </FormGroup>
      <FormGroup inline label="Use TradingView" justifyContent="space-between" alignItems="center">
        <Toggle
          checked={overrides.useTradingView ?? DEFAULT_WL_ORG_CONFIG.useTradingView}
          onChange={checked => setOverrides({ ...overrides, useTradingView: checked })}
        />
      </FormGroup>
      <FormGroup inline label="Enable non-native OHLC resolutions" justifyContent="space-between" alignItems="center">
        <Toggle
          checked={!!overrides.enableNonNativeOHLCResolutions}
          onChange={checked => setOverrides({ ...overrides, enableNonNativeOHLCResolutions: checked })}
        />
      </FormGroup>
      <FormGroup
        inline
        label="Show volume in historical price chart"
        justifyContent="space-between"
        alignItems="center"
      >
        <Toggle
          checked={(overrides.hideTradingViewVolume ?? DEFAULT_WL_ORG_CONFIG.hideTradingViewVolume) === false}
          onChange={checked => setOverrides({ ...overrides, hideTradingViewVolume: !checked })}
        />
      </FormGroup>
    </Dialog>
  );
};

const EXAMPLE_NAV_ITEMS: CustomerUserConfigNavigationItem[] = [
  {
    label: 'Market',
    path: '/',
    icon: IconName.ChartBarBox,
    requiredPermissions: [
      [WL_ACTION.INITIATE_ORDER, WL_ACTION.INITIATE_RFQ],
      WL_ACTION.SHOW_CANDLESTICKS_AND_MARKETDATA,
    ],
  },
  {
    label: 'Trading',
    path: '/trading',
    layoutComponent: CustomerUserConfigLayoutType.TradingLayout,
    icon: IconName.ChartLine,
    requiredPermissions: [
      [WL_ACTION.INITIATE_ORDER, WL_ACTION.INITIATE_RFQ],
      WL_ACTION.SHOW_CANDLESTICKS_AND_MARKETDATA,
    ],
  },
  {
    label: 'Portfolio',
    path: '/portfolio',
    icon: IconName.ChartDonut,
    layoutComponent: CustomerUserConfigLayoutType.PortfolioLayout,
    requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_BALANCES],
  },
  {
    label: 'Settings',
    icon: IconName.Cog,
    path: '/settings',
    layoutComponent: CustomerUserConfigLayoutType.SettingsLayout,
    requiredPermissions: ['settings.view'],
  },
  {
    label: 'Talos',
    icon: IconName.PresentationChartBar,
    path: '//talos.com',
    target: '_self',
    requiredPermissions: ['settings.view'],
  },
  {
    label: 'API docs',
    icon: IconName.ExternalLink,
    path: '//docs.talostrading.com',
    requiredPermissions: ['settings.view'],
  },
];
